import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Filter, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fyOf = (d) => (d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1);

export const EMPTY_LEDGER_FILTERS = { fy: '', from: '', to: '', types: [], q: '' };

export const applyLedgerFilters = (entries, f) => {
  let from = f.from, to = f.to;
  if (f.fy) { from = from || `${f.fy}-04-01`; to = to || `${+f.fy + 1}-03-31`; }
  const ql = (f.q || '').trim().toLowerCase();
  return (entries || []).filter(e => {
    const d = (e.date || '').slice(0, 10);
    if (from && d && d < from) return false;
    if (to && d && d > to) return false;
    if (f.types.length && !f.types.includes(e.type)) return false;
    if (ql && !`${e.reference || ''} ${e.description || ''} ${e.type || ''} ${e.payment_mode || ''}`.toLowerCase().includes(ql)) return false;
    return true;
  });
};

// Filter bar for a ledger entries list + Excel export of the same filtered view.
export const LedgerFilters = ({ clientId, clientName, entries, filters, setFilters, role }) => {
  const [open, setOpen] = useState(false);
  const types = useMemo(() => [...new Set((entries || []).map(e => e.type).filter(Boolean))], [entries]);
  const years = useMemo(() => {
    const ys = new Set((entries || []).map(e => (e.date || '').slice(0, 4)).filter(y => /^\d{4}$/.test(y)).map(Number));
    ys.add(fyOf(new Date()));
    return [...ys].sort((a, b) => b - a);
  }, [entries]);
  const active = (filters.fy ? 1 : 0) + (filters.from ? 1 : 0) + (filters.to ? 1 : 0) + filters.types.length + (filters.q ? 1 : 0);
  const set = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggleType = (t) => set('types', filters.types.includes(t) ? filters.types.filter(x => x !== t) : [...filters.types, t]);

  const download = async () => {
    try {
      let from = filters.from, to = filters.to;
      if (filters.fy) { from = from || `${filters.fy}-04-01`; to = to || `${+filters.fy + 1}-03-31`; }
      const res = await axios.get(`${API}/clients/${clientId}/ledger.xlsx`, {
        params: { date_from: from || '', date_to: to || '', types: filters.types.join(','), q: filters.q || '', role: role || '' },
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `${(clientName || 'ledger').replace(/[^\w]+/g, '_')}_ledger.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(url), 200);
    } catch { toast.error('Excel download failed'); }
  };

  return (
    <div className="border-b border-zinc-800/60 bg-zinc-900/40" data-testid="ledger-filters">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2">
        <button onClick={() => setOpen(o => !o)} data-testid="ledger-filters-toggle"
          className={`flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border ${active ? 'border-amber-500/60 text-amber-300 bg-amber-500/10' : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'}`}>
          <Filter size={11} /> Filters{active ? ` · ${active}` : ''}
        </button>
        <input value={filters.q} onChange={e => set('q', e.target.value)} placeholder="Search ref / description…" data-testid="ledger-filter-q"
          className="flex-1 min-w-[140px] bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-[11px] text-zinc-200 placeholder-zinc-600" />
        <select value={filters.fy} onChange={e => set('fy', e.target.value)} data-testid="ledger-filter-fy"
          className="bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-[11px] text-zinc-200">
          <option value="">All years</option>
          {years.map(y => <option key={y} value={y}>FY {y}-{String(y + 1).slice(2)}</option>)}
        </select>
        {active > 0 && <button onClick={() => setFilters(EMPTY_LEDGER_FILTERS)} className="text-zinc-500 hover:text-zinc-200" title="Clear" data-testid="ledger-filters-clear"><X size={12} /></button>}
        <button onClick={download} data-testid="ledger-xlsx" className="flex items-center gap-1 text-[11px] text-zinc-300 hover:text-amber-300 border border-zinc-700 rounded-lg px-2 py-1">
          <Download size={11} /> Excel
        </button>
      </div>
      {open && (
        <div className="px-3 pb-2 flex flex-wrap items-center gap-2" data-testid="ledger-filters-panel">
          <label className="text-[10px] text-zinc-500">From <input type="date" value={filters.from} onChange={e => set('from', e.target.value)} data-testid="ledger-filter-from" className="ml-1 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px] text-zinc-200" /></label>
          <label className="text-[10px] text-zinc-500">To <input type="date" value={filters.to} onChange={e => set('to', e.target.value)} data-testid="ledger-filter-to" className="ml-1 bg-zinc-950 border border-zinc-800 rounded px-1.5 py-0.5 text-[11px] text-zinc-200" /></label>
          <div className="flex flex-wrap gap-1">
            {types.map(t => (
              <button key={t} onClick={() => toggleType(t)} data-testid={`ledger-filter-type-${t.replace(/\W+/g, '-')}`}
                className={`text-[10px] px-2 py-0.5 rounded-full border ${filters.types.includes(t) ? 'border-amber-500/60 text-amber-300 bg-amber-500/10' : 'border-zinc-700 text-zinc-400'}`}>{t}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
